import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import PropertyCard from "@/components/PropertyCard";
import { useLazyList, LazyMore } from "@/lib/useLazyList";

const MORE = { de: "Weitere {n} Objekte anzeigen", en: "Show {n} more properties", es: "Mostrar {n} alojamientos más", nl: "Nog {n} objecten tonen", pl: "Pokaż {n} kolejnych obiektów", fr: "Afficher {n} autres hébergements", it: "Mostra altri {n} alloggi" };
import { lazy, Suspense } from "react";
const PropertiesMap = lazy(() => import("@/components/PropertiesMap").then((m) => ({ default: m.PropertiesMap })));
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSeo, useJsonLd, breadcrumbLd } from "@/lib/useSeo";
import { WeatherBlocks } from "@/components/WeatherBlocks";
import { positionCss } from "@/components/admin/ImagePositionPicker";
import { richText } from "@/lib/richText";
import { useTextVars, applyVars } from "@/lib/textVars";
import { DestinationFacts } from "@/components/DestinationFacts";
import { siteMobileSrcSet, MOBILE_MEDIA } from "@/lib/images";
import { islandUrl } from "@/lib/translations";

// Lange H1 kleiner setzen, damit sie auf max. 2 Zeilen bleibt (Insel-/Regions-/Orts- und Anlagenseiten)
export const h1Size = (h) => { const n = (h || "").length; return n > 65 ? "text-2xl sm:text-3xl lg:text-4xl" : n > 45 ? "text-3xl sm:text-4xl lg:text-5xl" : "text-4xl sm:text-5xl lg:text-6xl"; };

export const LandingPage = ({ heroImage, heroPosition = "center", heroAlt, badge, content: raw, properties, propertiesTitle, ctaLabel, ctaTo, facts, children, afterIntro = null, asideExtra = null, afterIntroSection = null, afterProperties = null, breadcrumb, jsonLd, island, place, testid, blocks, statsName }) => {
  const { lang, t } = useLang();
  // Erst 24 Objektkarten, Rest beim Scrollen – Inselseiten bleiben klein
  const lazy = useLazyList(properties || [], 24, `${(properties || []).length}|${(properties || [])[0]?.id || ""}`);
  // Mobil: langer Einleitungstext + Klima/Galerie/Fakten eingeklappt („Mehr lesen“), damit Regionen und Objekte sofort sichtbar sind; ab md alles offen
  const [more, setMore] = useState(false);
  const collapsible = !!(afterIntro || facts) || (raw.intro || "").length > 500;
  const folded = collapsible && !more;
  const MORE = { de: "Mehr lesen", en: "Read more", es: "Leer más", nl: "Meer lezen", pl: "Czytaj więcej", fr: "Lire la suite", it: "Leggi tutto" };
  const LESS = { de: "Weniger anzeigen", en: "Show less", es: "Mostrar menos", nl: "Minder tonen", pl: "Pokaż mniej", fr: "Réduire", it: "Mostra meno" };
  // Textvariablen ({insel}, {ort}, {firma} …) in allen Inhalten ersetzen
  const vars = useTextVars({ island, place });
  const v = (x) => applyVars(x, vars);
  const c = { ...raw, h1: v(raw.h1), intro: v(raw.intro), seo_title: v(raw.seo_title), meta_description: v(raw.meta_description),
    sections: (raw.sections || []).map((s) => (s.block ? s : { ...s, heading: v(s.heading), text: v(s.text) })), faq: (raw.faq || []).map((f) => ({ ...f, question: v(f.question), answer: v(f.answer) })) };
  useSeo({ title: c.seo_title, description: c.meta_description, image: heroImage, lang });
  useJsonLd([
    jsonLd,
    (c.faq || []).length ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: c.faq.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })) } : null,
    breadcrumbLd(breadcrumb),
  ]);

  const [heroBroken, setHeroBroken] = useState(false);
  const heroMobile = !heroBroken ? siteMobileSrcSet(heroImage) : "";
  return (
    <article data-testid={testid}>
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <picture>
          {heroMobile && <source media={MOBILE_MEDIA} type="image/webp" srcSet={heroMobile} sizes="100vw" data-testid="landing-hero-mobile-source" />}
          <img src={heroImage} alt={heroAlt || c.h1} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: positionCss(heroPosition) }} fetchPriority="high" decoding="async" onError={() => setHeroBroken(true)} data-testid="landing-hero-img" />
        </picture>
        <div className="absolute inset-0 hero-gradient-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-14 pt-32 w-full">
          {breadcrumb && (
            <nav className="text-xs text-white/80 mb-3 flex gap-2" data-testid="landing-breadcrumb">
              {breadcrumb.map((b, i) => (
                <span key={b.to} className="flex gap-2">{i > 0 && <span>/</span>}{i < breadcrumb.length - 1 ? <Link to={b.to} className="hover:text-white underline-offset-2 hover:underline">{b.name}</Link> : <span>{b.name}</span>}</span>
              ))}
            </nav>
          )}
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-white/90 font-semibold mb-4">{badge}</span>
          <h1 className={`font-serif text-white leading-[1.1] max-w-5xl ${h1Size(c.h1)}`} data-testid="landing-h1">{c.h1}</h1>
        </div>
      </section>

      <section className={`max-w-7xl mx-auto px-6 lg:px-10 py-16 grid ${facts ? "lg:grid-cols-3" : ""} gap-12`}>
        <div className="lg:col-span-2 min-w-0">
          <p className={`text-base md:text-lg text-stone-700 leading-relaxed whitespace-pre-line ${folded ? "line-clamp-6 md:line-clamp-none" : ""}`} data-testid="landing-intro">{richText(c.intro)}</p>
          {collapsible && (
            <button type="button" onClick={() => setMore((v) => !v)} aria-expanded={more} className="md:hidden mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand" data-testid="landing-read-more">
              {(more ? LESS : MORE)[lang] || MORE.en}<ChevronDown className={`w-4 h-4 transition-transform ${more ? "rotate-180" : ""}`} />
            </button>
          )}
          {statsName && <DestinationFacts properties={properties} name={statsName} kind={place ? "place" : "island"} lang={lang} />}
          <div className={folded ? "hidden md:block" : ""}>{afterIntro}</div>
        </div>
        {facts && (
          <aside className={`grid-cols-2 gap-4 content-start ${folded ? "hidden md:grid" : "grid"}`} data-testid="landing-facts">
            {island && <WeatherBlocks island={island} lang={lang} />}
            {facts.filter((f) => (f.value || "").trim()).map((f) => (
              <div key={f.label} className="rounded-2xl bg-tint p-4">
                <div className="text-[10px] uppercase tracking-widest text-brand">{f.label}</div>
                <div className="text-sm text-stone-800 mt-1 leading-snug">{f.value}</div>
              </div>
            ))}
            {asideExtra && <div className="col-span-2">{asideExtra}</div>}
          </aside>
        )}
      </section>
      {afterIntroSection}

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-8" data-testid="landing-properties-title">{propertiesTitle}</h2>
        {properties.length ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">{lazy.shown.map((p) => <PropertyCard key={p.id} property={p} />)}</div>
            <LazyMore lazy={lazy} label={MORE[lang] || MORE.en} className="-mt-6 mb-6" />
            {ctaTo && (
              <div className="mb-10 flex flex-wrap gap-3">
                <Link to={ctaTo} className="inline-flex items-center h-10 px-5 rounded-md bg-brand/75 hover:bg-brand text-white text-sm font-medium transition-colors" data-testid="landing-cta">{ctaLabel}</Link>
              </div>
            )}
            <Suspense fallback={<div className="h-[420px] rounded-2xl bg-stone-100 animate-pulse" />}><PropertiesMap properties={properties} lang={lang} /></Suspense>
          </>
        ) : place ? (
          <p className="text-stone-500" data-testid="landing-no-properties">
            {t.island.nonePlace.replace("{n}", place.name)}{" "}
            <Link to={islandUrl(island)} className="text-brand underline underline-offset-2 hover:text-brand-dark" data-testid="landing-no-properties-island-link">{t.island.nonePlaceLink.replace("{n}", t.islands[island] || island)}</Link>
          </p>
        ) : <p className="text-stone-500" data-testid="landing-no-properties">{t.island.none}</p>}
        {children && <div data-testid="landing-children">{children}</div>}
      </section>
      {afterProperties}

      {(c.sections || []).length > 0 && (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-20 space-y-10" data-testid="landing-sections">
          {c.sections.map((s, i) => s.block
            ? (blocks?.[s.block] ? <div key={i} data-testid={`landing-block-${s.block}`}>{typeof blocks[s.block] === "function" ? blocks[s.block](s) : blocks[s.block]}</div> : null)
            : <div key={i}><h2 className="font-serif text-2xl md:text-3xl text-stone-900 mb-3">{s.heading}</h2><p className="text-stone-700 leading-relaxed whitespace-pre-line">{richText(s.text)}</p></div>)}
        </section>
      )}

      {(c.faq || []).length > 0 && (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-20" data-testid="landing-faq">
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-6">{t.island.faq}</h2>
          <Accordion type="single" collapsible className="border-t border-stone-200">
            {c.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`landing-faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold text-stone-900">{f.question}</AccordionTrigger>
                <AccordionContent className="text-stone-700 leading-relaxed whitespace-pre-line">{richText(f.answer)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

    </article>
  );
};
